import React from 'react';
import { Link } from 'react-router-dom';
import { Users, BookOpen, Eye, Lock, Settings, LogOut, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import PadaidLogo from '../../components/ui/PadaidLogo';

const GuestDashboard = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-secondary/5">
      {/* Header */}
      <header className="bg-card/50 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <PadaidLogo size="md" showText={true} />
              <div className="ml-4">
                <span className="text-sm text-muted-foreground">Espace</span>
                <h1 className="text-lg font-semibold text-secondary">Invité</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Paramètres
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/">
                  <LogOut className="h-4 w-4 mr-2" />
                  Déconnexion
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Bienvenue en tant qu'invité
          </h2>
          <p className="text-muted-foreground">
            Découvrez PADAID et explorez nos ressources publiques
          </p>
        </div>

        {/* Guest Access Notice */}
        <Alert className="mb-8 border-secondary/20 bg-secondary/5">
          <Eye className="h-4 w-4" />
          <AlertDescription>
            En tant qu'invité, vous avez un accès limité aux ressources. 
            <Link to="/membership" className="ml-1 text-secondary hover:underline font-medium">
              Devenez membre pour débloquer toutes les fonctionnalités
            </Link>
          </AlertDescription>
        </Alert>

        {/* Available Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="hover:shadow-lg transition-shadow border-green-500/20">
            <CardHeader className="text-center">
              <BookOpen className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <CardTitle className="text-lg">Articles Publics</CardTitle>
              <CardDescription>Lisez nos articles ouverts à tous</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-green-500 hover:bg-green-600" asChild>
                <Link to="/blog">Lire maintenant</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-blue-500/20">
            <CardHeader className="text-center">
              <Users className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <CardTitle className="text-lg">À Propos</CardTitle>
              <CardDescription>Découvrez notre mission et nos fondateurs</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-blue-500 hover:bg-blue-600" asChild>
                <Link to="/founders">En savoir plus</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-purple-500/20">
            <CardHeader className="text-center">
              <Eye className="h-8 w-8 text-purple-500 mx-auto mb-2" />
              <CardTitle className="text-lg">Aperçu Galerie</CardTitle>
              <CardDescription>Quelques images de nos actions</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-purple-500 hover:bg-purple-600" asChild>
                <Link to="/gallery">Découvrir</Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Restricted Content */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4 flex items-center">
            <Lock className="mr-2 h-5 w-5 text-muted-foreground" />
            Contenu Réservé aux Membres
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="opacity-75 border-muted">
              <CardHeader>
                <CardTitle className="flex items-center text-muted-foreground">
                  <Lock className="mr-2 h-4 w-4" />
                  Articles Exclusifs
                </CardTitle>
                <CardDescription>
                  Analyses approfondies et rapports de terrain réservés aux membres
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" disabled className="w-full">
                  Accès membre requis
                </Button>
              </CardContent>
            </Card>

            <Card className="opacity-75 border-muted">
              <CardHeader>
                <CardTitle className="flex items-center text-muted-foreground">
                  <Lock className="mr-2 h-4 w-4" />
                  Galerie Complète
                </CardTitle>
                <CardDescription>
                  Toutes nos photos et vidéos de missions humanitaires
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" disabled className="w-full">
                  Accès membre requis
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Upgrade Prompt */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card className="bg-secondary/5 border-secondary/20">
            <CardHeader>
              <CardTitle className="text-secondary">Pourquoi devenir membre ?</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <ArrowRight className="w-4 h-4 text-secondary mr-3" />
                  <span>Accès complet aux articles et analyses</span>
                </li>
                <li className="flex items-center">
                  <ArrowRight className="w-4 h-4 text-secondary mr-3" />
                  <span>Galerie complète avec toutes les missions</span>
                </li>
                <li className="flex items-center">
                  <ArrowRight className="w-4 h-4 text-secondary mr-3" />
                  <span>Newsletter mensuelle exclusive</span>
                </li>
                <li className="flex items-center">
                  <ArrowRight className="w-4 h-4 text-secondary mr-3" />
                  <span>Participation aux événements PADAID</span>
                </li>
                <li className="flex items-center">
                  <ArrowRight className="w-4 h-4 text-secondary mr-3" />
                  <span>Interaction avec la communauté</span>
                </li>
              </ul>
              <Button className="w-full mt-6 bg-secondary hover:bg-secondary/90 text-black" asChild>
                <Link to="/membership">
                  Devenir Membre
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contenu Disponible</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Articles publics</p>
                    <p className="text-xs text-muted-foreground">Introduction à PADAID et nos valeurs</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Profils des fondateurs</p>
                    <p className="text-xs text-muted-foreground">Abdou, ARBAB et Moubarak</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Aperçu galerie</p>
                    <p className="text-xs text-muted-foreground">Sélection d'images de nos missions</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Contact</p>
                    <p className="text-xs text-muted-foreground">Formulaire de contact public</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GuestDashboard;